import { Query } from '@apollo/client/react/components';
import {
  calculateValidPeriod,
  createEventData,
  logOutIntercomUser,
  pushToDataLayer,
  updateIntercomUser,
} from '@arnold/common';
import { useTranslation } from 'react-i18next';
import { Route, RouteProps } from 'react-router-dom';
import {
  AccessTokenStatus,
  FeEventType,
  IsAccessTokenValidQuery,
  useLogFeEventMutation,
  UserOrgRole,
  UserSysRole,
} from '../generated/hooks';
import { isAccessTokenValidQuery } from '../graphql/queries';
import auth from '../lib/auth';
import PageLayout from './PageLayout';
import { Loading } from '.';
import { ACCESS_TOKEN_NOT_FOUND_ERROR_MESSAGE } from '@arnold/core';

const PrivateRouter = ({
  component: Component,
  setLocalization,
  ...rest
}: RouteProps & { setLocalization: (lang: string) => any }) => {
  const { i18n } = useTranslation();
  const [logFEEvent] = useLogFeEventMutation();
  return (
    <Route
      {...rest}
      render={(props: any) => {
        if (Component != null) {
          return (
            <Query<IsAccessTokenValidQuery>
              query={isAccessTokenValidQuery}
              onError={() => {
                logOutIntercomUser();
              }}
              fetchPolicy="network-only"
              onCompleted={async (responseData) => {
                if (responseData?.isAccessTokenValid.status !== AccessTokenStatus.Valid) {
                  // Consent is now implied at login; restore consent check when removing this workaround.
                  auth.removeUserCookies();
                }
                const user = responseData.isAccessTokenValid.user;
                if (user) {
                  const organizationRoleForIntercom =
                    user.systemRole === UserSysRole.SysAdmin ||
                    user.respondents?.find((r) => r.organizationRole === UserOrgRole.OrgAdmin)
                      ? UserOrgRole.OrgAdmin
                      : UserOrgRole.OrgRespondent;
                  updateIntercomUser({
                    user_id: user.id,
                    user_hash: auth.getIntercomHash(),
                    name: `${user.firstname} ${user.surname}`,
                    email: user.username,
                    marketingConsent: user.marketingConsent,
                    language_override: i18n.language,
                    'Organization role': organizationRoleForIntercom,
                    companies: user.respondents?.map(({ organization }) => {
                      const validPeriod =
                        organization?.productVersionHistory && calculateValidPeriod(organization.productVersionHistory);
                      return {
                        id: organization.id,
                        name: organization.name,
                        'System name': organization.systemName,
                        plan: validPeriod?.product,
                        'Primary language': organization.primaryLanguageCode,
                        'All languages': organization.languages.map(({ code }) => code).join(', '),
                        'Company ID': organization.companyId,
                        'Licence count': validPeriod?.licenceCount,
                        'Valid from': validPeriod?.validFrom,
                        'Valid to': validPeriod?.validTo,
                      };
                    }),
                  });
                  logFEEvent({
                    variables: {
                      eventType: FeEventType.OneplaceLoaded,
                      payload: {
                        respondentId: user.respondents?.[0]?.id,
                        originatorId: user.id,
                        orgRole: organizationRoleForIntercom,
                      },
                    },
                  });
                  if (rest.location?.search) {
                    pushToDataLayer({
                      userId: user.id,
                      event: 'ux.talk-access-with-token',
                      ...createEventData('user', 'talk-access-with-token', 'talk-access-with-token'),
                    });
                  }
                }
              }}
            >
              {({ loading, error, data }) => {
                if (loading) {
                  return <Loading />;
                }

                if (
                  (data && data.isAccessTokenValid.status !== AccessTokenStatus.Valid) ||
                  error?.name === 'AccessTokenNotFound' ||
                  error?.message === ACCESS_TOKEN_NOT_FOUND_ERROR_MESSAGE
                ) {
                  auth.removeUserCookies();
                  auth.redirectToLogin();
                  return <></>;
                }
                if (error) {
                  throw error;
                }

                const allowedLanguages = ['en', 'cs', 'sk', 'hr'];
                let initLanguage = localStorage.getItem('i18nextLng');
                if (initLanguage && !allowedLanguages.includes(initLanguage)) {
                  initLanguage = 'en';
                }

                if (data) {
                  return (
                    <PageLayout
                      setLocalization={setLocalization}
                      user={data.isAccessTokenValid.user}
                      allowedLanguages={allowedLanguages}
                      initLanguage={initLanguage}
                    >
                      <Component {...props} user={data.isAccessTokenValid.user} />
                    </PageLayout>
                  );
                }
                return null;
              }}
            </Query>
          );
        }

        void auth.logout().then((shouldRedirectToLogin) => {
          if (shouldRedirectToLogin) {
            auth.redirectToLogin();
          }
        });
      }}
    />
  );
};

export default PrivateRouter;
